import * as React from 'react';
import { useEffect } from 'react';
import { FormattedMessage } from "react-intl";

import {
    selectCurrentResidenceType,
    selectDisposalForm,
    selectInhabitDate,
    selectMaxArea,
    selectMaxPrice,
    selectMinArea,
    selectMinPrice,
    selectAttributes,
    selectProjectStatus,
    selectRooms,
    selectShowings,
    useSearchStore
} from 'features/search/store/useSearchStore';
import { LOCALE, SITE } from 'features/ui/enums';
import { SearchTypes } from 'features/search/enums';
import {useModal} from "features/modal/hooks/useModal";
import { useSearchData } from 'features/search/services/SearchService/query';
import { getFormattedMessage } from "features/services/utils/language-helper";
import { useDeviceStore, selectIsMobile } from 'features/ui/store/useDeviceStore';
import { getCurrentLocale, getCurrentSite } from 'features/services/utils/env-helper';
import { DisposalForm, AttributeType, StageStatus } from 'features/search/filters/enums';
import { ELEVATOR_FILTER, PARKING_FILTER } from 'features/search/helpers';
import { FILTERS_MIN_PRICE, FILTERS_MAX_PRICE, FILTERS_MAX_AREA, FILTERS_MIN_AREA, ShowingFilter, InhabitDateFilter } from 'features/search/filters/constants';

import './style.scss';

import { A } from '@atoms/LinksAndButtons/A';
import { P } from '@atoms/Text/P';
import { Switch } from '@atoms/FormElements/Switch';
import { ResidenceFilterSection } from './_section';
import { CheckboxList } from '../formComponents/checkboxList';
import { ResidenceTypeButtons } from './_residenceTypeButtons';
import { RadioButtonList } from '../formComponents/radioButtonList';
import { FormRangeSlider } from '../formComponents/FormRangeSlider';
import { Button, ButtonColor, ButtonSize, ButtonType } from '@atoms/LinksAndButtons/Button';

export enum FilterFormType {
    DEFAULT = 'default',
    RESIDENCE_EXPLORER = 'residence-explorer',
    PREMISES = 'premises',
}

export type FilterSection = 'price' | 'rooms' | 'area' | 'type';

interface Props {
    type: FilterFormType;
    scrollToSection?: FilterSection;
}

export const ResidenceFilterForm: React.FunctionComponent<Props> = ({type, scrollToSection}) => {
    const locale = getCurrentLocale();
    const site = getCurrentSite();
    const isMobile = useDeviceStore(selectIsMobile);
    const storeUpdateFilters = useSearchStore(state => state.updateFilters);
    const resetFilters = useSearchStore(state => state.resetFilters);
    const { close } = useModal();
    const bodyRef = React.useRef<HTMLDivElement>(null);

    const updateFilters = storeUpdateFilters;
    const priceSectionRef = React.useRef<HTMLDivElement>(null);
    const roomsSectionRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!scrollToSection || !bodyRef.current) return;

        const target = scrollToSection === 'price' ? priceSectionRef.current : roomsSectionRef.current;
        if (target && bodyRef.current) {
            bodyRef.current.scrollTop = target.offsetTop - bodyRef.current.offsetTop;
        }
    }, [scrollToSection]);

    const minPrice = useSearchStore(selectMinPrice);
    const maxPrice = useSearchStore(selectMaxPrice);
    const onPriceChange = (values: number[]) => {
        updateFilters({ minPrice: values[0], maxPrice: values[1] });
    }

    const inhabitDate = useSearchStore(selectInhabitDate);

    const rooms = useSearchStore(selectRooms);

    const minArea = useSearchStore(selectMinArea);
    const maxArea = useSearchStore(selectMaxArea);
    const onChangeArea = (values: number[]) => {
        updateFilters({ minArea: values[0], maxArea: values[1] });
    }

    const projectStatus = useSearchStore(selectProjectStatus);

    const disposalForm = useSearchStore(selectDisposalForm);

    const showings = useSearchStore(selectShowings);

    const attributes = useSearchStore(selectAttributes);

    const handleReset = () => {
        resetFilters(true);
        close();
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        close();
    };

    const currentResidenceType = useSearchStore(selectCurrentResidenceType);
    const { searchResult: filteredSearch, searchCount } = useSearchData();
    const renderButtonText = () => {
        let typeLabel = '';
        let count = '';

        switch (currentResidenceType) {
            case SearchTypes.AREA:
                typeLabel = (filteredSearch.area.length === 1)
                    ? getFormattedMessage('jm.components.ResidenceFilters.area')
                    : getFormattedMessage('jm.components.ResidenceFilters.areas');
                count = searchCount.areas.toString();
                break;
            case SearchTypes.OBJECTS:
                typeLabel = getFormattedMessage('jm.components.ResidenceFilters.objects');
                count = searchCount.objects.toString();
                break;
            case SearchTypes.STAGE:
                typeLabel = (filteredSearch.area.length === 1)
                    ? getFormattedMessage('jm.components.ResidenceFilters.project')
                    : getFormattedMessage('jm.components.ResidenceFilters.projects');
                count = searchCount.stages.toString();
                break;
        }
        if(type === FilterFormType.RESIDENCE_EXPLORER) typeLabel = getFormattedMessage('jm.components.ResidenceFilters.objects');

        return getFormattedMessage('jm.components.ResidenceFilters.seeResidences', { count: count, type: typeLabel });
    };

    return (
        <form
            onSubmit={handleSubmit}
            className="residence-filter-form"
        >
            <div className='residence-filter-form__header'>
                <P center>{getFormattedMessage("jm.components.ResidenceFilters.header")}</P>
            </div>

            <div className='residence-filter-form__body' ref={bodyRef}>
                {/* Residence type */}
                {type === FilterFormType.DEFAULT && locale !== LOCALE.FI && site !== SITE.SENIORGARDEN && site !== SITE.BORATT &&
                    <ResidenceFilterSection
                        headline={getFormattedMessage("jm.components.ResidenceFilters.residenceType")}
                    >
                        <ResidenceTypeButtons />
                    </ResidenceFilterSection>
                }

                {/* Price */}
                <ResidenceFilterSection
                    ref={priceSectionRef}
                    headline={getFormattedMessage('jm.components.ResidenceFilters.price')}
                >
                    <FormRangeSlider
                        onSliderChange={onPriceChange}
                        min={FILTERS_MIN_PRICE}
                        max={FILTERS_MAX_PRICE}
                        defaultValue={[minPrice, maxPrice]}
                        label={null}
                        valueSuffix={getFormattedMessage('currency')}
                        maxValueSuffix={getFormattedMessage('currency') + '+'}
                        step={100000}
                        minRange={100000}
                    />
                </ResidenceFilterSection>

                {/* Rooms */}
                <ResidenceFilterSection
                    ref={roomsSectionRef}
                    headline={getFormattedMessage("jm.components.ResidenceFilters.rooms")}
                >
                    <CheckboxList
                        horizontal
                        label={getFormattedMessage("jm.components.ResidenceFilters.rooms")}
                        hideLabel
                        value={rooms}
                        options={[
                            { value: '1', label: '1' },
                            { value: '2', label: '2' },
                            { value: '3', label: '3' },
                            { value: '4', label: '4' },
                            { value: '5', label: '5+' },
                        ]}
                        onCustomChange={(values) => updateFilters({ rooms: values })}
                    />
                </ResidenceFilterSection>

                {/* Area */}
                <ResidenceFilterSection
                    headline={getFormattedMessage("jm.components.ResidenceFilters.primaryArea")}
                >
                    <FormRangeSlider
                        defaultValue={[minArea, maxArea]}
                        min={FILTERS_MIN_AREA}
                        max={FILTERS_MAX_AREA}
                        minRange={1}
                        valueSuffix={getFormattedMessage("jm.components.ResidenceFilters.primaryAreaSuffix")}
                        label={null}
                        onSliderChange={onChangeArea}
                    />
                </ResidenceFilterSection>

                {/* Inhabit date */}
                {type === FilterFormType.DEFAULT && <ResidenceFilterSection
                    headline={getFormattedMessage("jm.components.ResidenceFilters.inhabitDate")}
                >
                    <RadioButtonList
                        label={getFormattedMessage("jm.components.ResidenceFilters.inhabitDate")}
                        hideLabel
                        value={inhabitDate}
                        options={[
                            { value: InhabitDateFilter.Now, label: getFormattedMessage('jm.components.InhabitDateLabel.immediately') },
                            { value: InhabitDateFilter.InSixMonths, label: getFormattedMessage('jm.components.InhabitDateLabel.sixMonths') },
                            { value: InhabitDateFilter.InMoreThenOneYear, label: getFormattedMessage('jm.components.InhabitDateLabel.oneYear') },
                        ]}
                        onCustomChange={(value) => updateFilters({ inhabitDate: value })}
                    />
                </ResidenceFilterSection>}

                {/* Project status */}
                {type === FilterFormType.DEFAULT && <ResidenceFilterSection
                    headline={getFormattedMessage("jm.components.ResidenceFilters.stageStatus")}
                >
                    <CheckboxList
                        label={getFormattedMessage("jm.components.ResidenceFilters.stageStatus")}
                        hideLabel
                        value={projectStatus}
                        options={[
                                    { value: StageStatus.FORSALE , label: getFormattedMessage('jm.components.ResidenceTags.selling') },
                                    site === SITE.FI 
                                        ? { value: StageStatus.PRESELLING , label: getFormattedMessage('jm.components.ResidenceTags.preSelling') }
                                        : { value: StageStatus.VIP , label: getFormattedMessage('jm.components.ResidenceFilters.vipSale') },
                                    { value: StageStatus.PLANNED , label: getFormattedMessage('jm.components.ResidenceTags.comming') },
                                ] }
                        onCustomChange={(values) => updateFilters({ projectStatus: values })}
                    />
                </ResidenceFilterSection>}

                {/* Disposal form */}
                {type === FilterFormType.DEFAULT && locale !== LOCALE.FI && site !== SITE.SENIORGARDEN && site !== SITE.BORATT &&
                    <ResidenceFilterSection
                        headline={getFormattedMessage("jm.components.ResidenceFilters.disposalForm")}
                    >
                        <CheckboxList
                            label={getFormattedMessage("jm.components.ResidenceFilters.disposalForm")}
                            hideLabel
                            value={disposalForm}
                            options={[
                                { value: DisposalForm.CONDOMINIUM, label: getFormattedMessage('jm.components.ResidenceFilters.condominium') },
                                { value: DisposalForm.OWNERSHIP, label: getFormattedMessage('jm.components.ResidenceFilters.ownership') }
                            ]}
                            onCustomChange={(values) => updateFilters({ disposalForm: values })}
                        />
                    </ResidenceFilterSection>
                }

                {/* Showings */}
                {type === FilterFormType.DEFAULT && <ResidenceFilterSection
                    headline={getFormattedMessage('jm.components.ResidenceFilters.showings')}
                >
                    <Switch
                        defaultChecked={showings === ShowingFilter.HasAny}
                        label={getFormattedMessage('jm.components.ResidenceFilters.withShowing')}
                        onClick={(checked) => {
                            const value = checked ? ShowingFilter.HasAny : ShowingFilter.IncludeAll;
                            updateFilters({ viewing: value });
                        }}
                    />
                </ResidenceFilterSection>}

                {/* Attributes */}
                {type === FilterFormType.DEFAULT && <ResidenceFilterSection
                    headline={getFormattedMessage("jm.components.ResidenceFilters.attributes")}
                >
                    <CheckboxList
                        label={getFormattedMessage("jm.components.ResidenceFilters.attributes")}
                        hideLabel
                        value={attributes}
                        options={[
                            { value: AttributeType.BALCONY, label: getFormattedMessage('jm.components.ResidenceFilters.balcony') },
                            ...(locale !== LOCALE.FI
                                ? [{ value: AttributeType.PATIO, label: getFormattedMessage('jm.components.ResidenceFilters.patio') }]
                                : []),
                            { value: AttributeType.TERRACE, label: getFormattedMessage('jm.components.ResidenceFilters.terrace') },
                            ...(locale === LOCALE.FI
                                ? [
                                    { value: AttributeType.SAUNA, label: getFormattedMessage('jm.components.ResidenceFilters.sauna') },
                                    { value: AttributeType.WALK_IN_CLOSET, label: getFormattedMessage('jm.components.ResidenceFilters.walkInCloset') },
                                ]
                                : []),
                            { value: PARKING_FILTER, label: getFormattedMessage('jm.components.ResidenceFilters.parking') },
                            { value: ELEVATOR_FILTER, label: getFormattedMessage('jm.components.ResidenceFilters.elevator') },
                        ]}
                        onCustomChange={(values) => updateFilters({ attributes: values })}
                    />
                </ResidenceFilterSection>}
            </div>

            <div className='residence-filter-form__footer'>
                <span><A
                    href="#"
                    title={getFormattedMessage('jm.components.ResidenceFilters.resetFilters')}
                    onClick={(e) => { e.preventDefault(); handleReset(); }}
                >
                    <FormattedMessage id="jm.components.ResidenceFilters.resetFilters" />
                </A></span>
                <Button
                    buttonColor={ButtonColor.CTA}
                    buttonSize={isMobile 
                        ? ButtonSize.FULL 
                        : ButtonSize.DEFAULT}
                    buttonType={ButtonType.DEFAULT}
                    submit
                >
                    {renderButtonText()}
                </Button>
            </div>
        </form>
    )
};
